// Custom hook for animated background effect
import { useEffect, useRef } from 'react';

interface UseAnimatedBackgroundProps {
  cardRef: React.RefObject<HTMLDivElement>;
  enabled?: boolean;
}

export const useAnimatedBackground = ({ cardRef, enabled = true }: UseAnimatedBackgroundProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const particlesRef = useRef<HTMLDivElement>(null);
  const animationFrameRef = useRef<number>();

  useEffect(() => {
    if (!enabled) return;
    
    const canvas = canvasRef.current;
    const particlesContainer = particlesRef.current;
    
    if (!canvas || !particlesContainer) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    // Mouse tracking disabled for performance

    // Reduced particle counts for better performance
    const depthLayers = [
      { name: 'layer-1', count: 15, sizeRange: [2.5, 5], maxDistance: 120, lineOpacity: 0.12 },
      { name: 'layer-2', count: 12, sizeRange: [3.5, 6.5], maxDistance: 140, lineOpacity: 0.14 },
      { name: 'layer-3', count: 10, sizeRange: [5, 9], maxDistance: 160, lineOpacity: 0.16 },
      { name: 'layer-4', count: 8, sizeRange: [4, 7.5], maxDistance: 130, lineOpacity: 0.13 }
    ];

    const particles: Array<{
      layer: string;
      elements: HTMLDivElement[];
      maxDistance: number;
      lineOpacity: number;
      positions: Array<{ x: number; y: number; vx: number; vy: number }>;
      connections: Map<string, { active: boolean; pulseIntensity: number }>;
    }> = [];

    depthLayers.forEach(layer => {
      const layerParticles: HTMLDivElement[] = [];
      const positions: Array<{ x: number; y: number; vx: number; vy: number }> = [];
      
      for (let i = 0; i < layer.count; i++) {
        const particle = document.createElement('div');
        particle.className = `absolute rounded-full ${layer.name}`;
        
        const size = Math.random() * (layer.sizeRange[1] - layer.sizeRange[0]) + layer.sizeRange[0];
        particle.style.width = size + 'px';
        particle.style.height = size + 'px';
        
        const x = Math.random() * 100;
        const y = Math.random() * 100;
        particle.style.left = x + '%';
        particle.style.top = y + '%';
        particle.style.animationDelay = Math.random() * 20 + 's';
        particle.style.transition = 'transform 0.3s ease-out, filter 0.3s ease-out, box-shadow 0.2s ease-out';
        
        particlesContainer.appendChild(particle);
        layerParticles.push(particle);
        
        // Store initial position and velocity
        positions.push({
          x: (x / 100) * window.innerWidth,
          y: (y / 100) * window.innerHeight,
          vx: 0,
          vy: 0
        });
      }
      
      particles.push({
        layer: layer.name,
        elements: layerParticles,
        maxDistance: layer.maxDistance,
        lineOpacity: layer.lineOpacity,
        positions: positions,
        connections: new Map()
      });
    });

    let lastFrameTime = 0;
    const targetFPS = 30; // Reduced from 60 for better performance
    const frameInterval = 1000 / targetFPS;
    const startTime = Date.now();
    
    const drawConnections = (currentTime: number) => {
      // Throttle to target FPS
      if (currentTime - lastFrameTime < frameInterval) {
        animationFrameRef.current = requestAnimationFrame(drawConnections);
        return;
      }
      lastFrameTime = currentTime;
      
      // Calculate simple pan only (removed rotation for performance)
      const elapsed = (Date.now() - startTime) / 1000;
      const panSpeed = 10; // Reduced from 15
      
      const panX = Math.sin(elapsed * 0.2) * panSpeed; // Slower movement
      const panY = Math.cos(elapsed * 0.15) * panSpeed;
      
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      // Simplified transformation (no rotation)
      ctx.save();
      ctx.translate(panX, panY);
      
   
      
      // Simplified particle updates (removed card magnification for performance)
      particles.forEach(layerData => {
        layerData.elements.forEach((particle, index) => {
          const pos = layerData.positions[index];
          
          // Simple damping
          pos.vx *= 0.98;
          pos.vy *= 0.98;
          
          // Update position
          pos.x += pos.vx;
          pos.y += pos.vy;
          
          // Keep particles within bounds
          if (pos.x < 0) { pos.x = 0; pos.vx *= -0.5; }
          else if (pos.x > canvas.width) { pos.x = canvas.width; pos.vx *= -0.5; }
          if (pos.y < 0) { pos.y = 0; pos.vy *= -0.5; }
          else if (pos.y > canvas.height) { pos.y = canvas.height; pos.vy *= -0.5; }
          
          // Update particle DOM position
          particle.style.left = `${(pos.x / canvas.width) * 100}%`;
          particle.style.top = `${(pos.y / canvas.height) * 100}%`;
        });
      });
      
      // Simplified connection drawing (removed card magnification and pulse effects)
      particles.forEach(layerData => {
        const elements = layerData.elements;
        const maxDistance = layerData.maxDistance;
        const maxDistanceSq = maxDistance * maxDistance;
        const baseOpacity = layerData.lineOpacity;
        
        // Cache particle positions
        const particlePositions: Array<{ x: number; y: number }> = [];
        for (let i = 0; i < elements.length; i++) {
          const rect = elements[i].getBoundingClientRect();
          particlePositions.push({
            x: rect.left + rect.width / 2,
            y: rect.top + rect.height / 2
          });
        }
        
        for (let i = 0; i < elements.length; i++) {
          const x1 = particlePositions[i].x;
          const y1 = particlePositions[i].y;
          
          for (let j = i + 1; j < elements.length; j++) {
            const x2 = particlePositions[j].x;
            const y2 = particlePositions[j].y;
            
            // Quick distance check
            const dx = x2 - x1;
            const dy = y2 - y1;
            const distanceSq = dx * dx + dy * dy;
            
            // Early exit if too far
            if (distanceSq > maxDistanceSq * 2) continue;
            
            const distance = Math.sqrt(distanceSq);
            
            if (distanceSq < maxDistanceSq) {
              const opacity = (1 - distance / maxDistance) * baseOpacity;
              const lineWidth = layerData.layer === 'layer-4' ? 1.5 :
                               layerData.layer === 'layer-3' ? 1.2 : 
                               layerData.layer === 'layer-2' ? 1 : 0.8;
              
              // Simple gradient
              const gradient = ctx.createLinearGradient(x1, y1, x2, y2);
              gradient.addColorStop(0, `rgba(102, 126, 234, ${opacity * 0.8})`);
              gradient.addColorStop(0.5, `rgba(255, 255, 255, ${opacity})`);
              gradient.addColorStop(1, `rgba(118, 75, 162, ${opacity * 0.8})`);
              
              ctx.strokeStyle = gradient;
              ctx.lineWidth = lineWidth;
              ctx.beginPath();
              ctx.moveTo(x1, y1);
              ctx.lineTo(x2, y2);
              ctx.stroke();
            }
          }
        }
      });
      
      // Restore canvas transformation
      ctx.restore();
      
      animationFrameRef.current = requestAnimationFrame(drawConnections);
    };
    
    drawConnections(0);

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      particlesContainer.innerHTML = '';
    };
  }, [cardRef, enabled]);

  return { canvasRef, particlesRef };
};
